import { ChangeEvent } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { DialogConfirm } from 'app/components/elements/DialogConfirm';
import { InputText } from 'app/components/elements/InputText';
import { useRoomContext } from 'contexts/RoomContext';
import { PhotoManagement } from 'app/pages/ValidationsPage/components/elements/PhotoManagement';
import type { AssignmentRow } from 'services/report';
import { useMaintenanceIssueDialog } from '../hooks/useMaintenanceIssueDialog';
import { IssueAssignmentFields } from './IssueAssignmentFields';
import {
  IssueDialogSection,
  IssueMetaGrid,
  IssuePhotosGrid,
  IssueWorkflowDialogLayout
} from './IssueWorkflowDialogLayout';

// The maintenance_issues table only supports ACTIVE/RESOLVED (no ARCHIVED, unlike
// Room/Asset/AdHoc issues), so this dialog uses a restricted status list.
const MAINTENANCE_STATUS_OPTIONS = [
  { label: 'Open', value: 'ACTIVE' },
  { label: 'Resolved', value: 'RESOLVED' }
];

export const MaintenanceIssueDialog = ({
  isOpen,
  issueId,
  item,
  onClose,
  onSaved
}: {
  isOpen: boolean;
  issueId?: number;
  item: AssignmentRow | null;
  onClose: () => void;
  onSaved?: () => void;
}) => {
  const { users } = useRoomContext();

  const {
    loading,
    issue,
    isEditing,
    criticalityLevels,
    assignmentForm,
    existingBeforePhotos,
    existingAfterPhotos,
    newBeforePhotos,
    setNewBeforePhotos,
    newAfterPhotos,
    setNewAfterPhotos,
    deletedPhotoIds,
    setDeletedPhotoIds,
    notes,
    setNotes,
    handleSave
  } = useMaintenanceIssueDialog({ isOpen, issueId, onSaved });

  return (
    <DialogConfirm
      isOpen={isOpen}
      onClose={onClose}
      textTitle="Maintenance Issue"
      textButton={isEditing ? 'Save' : 'Edit'}
      textCancel="Close"
      onSubmit={handleSave}
      isIconClose
      maxWidth={1000}>
      {loading ? (
        <Box display="flex" alignItems="center" justifyContent="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : (
        <IssueWorkflowDialogLayout
          main={
            <>
              <IssueDialogSection title="Maintenance Details">
                <IssueMetaGrid
                  items={[
                    { label: 'Client', value: issue?.assetClientName },
                    { label: 'Location', value: issue?.location },
                    { label: 'Asset', value: issue?.assetName },
                    { label: 'Asset Device Id', value: issue?.assetDeviceId },
                    { label: 'Asset Device Name', value: issue?.assetDeviceName },
                    { label: 'Description', value: issue?.description }
                  ]}
                />
              </IssueDialogSection>

              <IssueDialogSection title="Assignment Information">
                <IssueAssignmentFields
                  control={assignmentForm.control}
                  isEditing={isEditing}
                  users={users || []}
                  criticalityLevels={criticalityLevels}
                  statusOptions={MAINTENANCE_STATUS_OPTIONS}
                />
                <InputText
                  title="Notes"
                  placeholder="Add notes"
                  inputProps={{
                    value: notes,
                    onChange: (
                      e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | string | number
                    ) => setNotes(typeof e === 'object' ? e.target.value : String(e)),
                    multiline: true,
                    minRows: 2,
                    maxRows: 4,
                    disabled: !isEditing,
                    sx: { padding: '10px 12px' }
                  }}
                />
              </IssueDialogSection>

              <IssueDialogSection title="Photo Documentation">
                <IssuePhotosGrid>
                  <PhotoManagement
                    sectionLabel="Issue Photos Before"
                    existingPhotos={existingBeforePhotos}
                    newPhotos={newBeforePhotos}
                    deletedPhotoIds={deletedPhotoIds}
                    disabled={!isEditing}
                    onAddPhotos={(files) => setNewBeforePhotos((prev) => [...prev, ...files])}
                    onDeleteExistingPhoto={(photoId) =>
                      photoId > 0 &&
                      setDeletedPhotoIds((prev) =>
                        prev.includes(photoId) ? prev : [...prev, photoId]
                      )
                    }
                    onRestorePhoto={(photoId) =>
                      setDeletedPhotoIds((prev) => prev.filter((id) => id !== photoId))
                    }
                    onRemoveNewPhoto={(index) =>
                      setNewBeforePhotos((prev) => prev.filter((_, i) => i !== index))
                    }
                    compactUpload
                  />
                  <PhotoManagement
                    sectionLabel="Issue Photos After"
                    existingPhotos={existingAfterPhotos}
                    newPhotos={newAfterPhotos}
                    deletedPhotoIds={deletedPhotoIds}
                    disabled={!isEditing}
                    onAddPhotos={(files) => setNewAfterPhotos((prev) => [...prev, ...files])}
                    onDeleteExistingPhoto={(photoId) =>
                      photoId > 0 &&
                      setDeletedPhotoIds((prev) =>
                        prev.includes(photoId) ? prev : [...prev, photoId]
                      )
                    }
                    onRestorePhoto={(photoId) =>
                      setDeletedPhotoIds((prev) => prev.filter((id) => id !== photoId))
                    }
                    onRemoveNewPhoto={(index) =>
                      setNewAfterPhotos((prev) => prev.filter((_, i) => i !== index))
                    }
                    compactUpload
                  />
                </IssuePhotosGrid>
              </IssueDialogSection>
            </>
          }
        />
      )}
    </DialogConfirm>
  );
};
